import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageSquare, Instagram, Facebook } from "lucide-react";
import { nav, business, telLink, smsLink, assets } from "@/data/site";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="logo-home" className="group flex shrink-0 items-center">
    <img
      src={assets.logo}
      alt="Benji's Hauling & Junk Removal logo"
      width="200" height="200"
      className="relative z-10 h-[76px] w-auto transition-transform duration-300 group-hover:scale-[1.05] sm:h-[104px]"
    />
    <span className="sr-only">Benji's Hauling &amp; Junk Removal</span>
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-shadow ${scrolled ? "border-border bg-cream/95 shadow-sm backdrop-blur" : "border-transparent bg-cream"}`}
    >
      <div className="container-x flex h-[92px] items-center justify-between sm:h-[120px]">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative rounded-md px-3.5 py-2 text-sm font-semibold tracking-tight transition-colors after:absolute after:bottom-1 after:left-3.5 after:right-3.5 after:h-0.5 after:origin-left after:rounded-full after:bg-gold after:transition-transform after:duration-300 ${
                  isActive
                    ? "text-gold-dark after:scale-x-100"
                    : "text-forest/80 hover:text-forest after:scale-x-0 hover:after:scale-x-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <a href={smsLink} data-testid="header-text" className="inline-flex items-center gap-2 rounded-md border border-forest/25 px-4 py-2.5 text-sm font-semibold text-forest transition-all hover:-translate-y-0.5 hover:bg-forest hover:text-cream">
            <MessageSquare className="h-4 w-4" strokeWidth={2.2} /> Text Us
          </a>
          <a href={telLink} data-testid="header-call" className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2.5 text-sm font-bold text-charcoal shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gold-dark hover:shadow-md">
            <Phone className="h-4 w-4" strokeWidth={2.2} /> Call Now
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="flex h-11 w-11 items-center justify-center rounded-md border border-forest/20 text-forest lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 top-[92px] z-40 lg:hidden sm:top-[120px] ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div
          className={`absolute inset-0 bg-charcoal/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        <nav
          className={`absolute left-0 right-0 top-0 origin-top border-b border-border bg-cream p-5 shadow-lg transition-transform duration-300 ${open ? "translate-y-0" : "-translate-y-4 opacity-0"}`}
          aria-label="Mobile"
        >
          <div className="flex flex-col">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `border-b border-border/70 py-3.5 text-base font-semibold tracking-tight ${isActive ? "text-gold-dark" : "text-forest"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-5 flex items-center gap-3">
            <a href={business.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-10 w-10 items-center justify-center rounded-md border border-forest/20 text-forest hover:bg-forest hover:text-cream">
              <Instagram className="h-5 w-5" />
            </a>
            <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="flex h-10 w-10 items-center justify-center rounded-md border border-forest/20 text-forest hover:bg-forest hover:text-cream">
              <Facebook className="h-5 w-5" />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
